'use client'

import { featureFlagsAtom } from '@polaris/core/stores'
import { ClientOnly } from '@quirks/react'
import { useAtomValue, useSetAtom } from 'jotai'
import Link from 'next/link'
import { useCallback, useState } from 'react'
import { useActivateAccount } from '@/src/hooks/account/use-activate-account'
import { usePendingTrades } from '@/src/hooks/trade/use-pending-trades'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { usePendingAccountDialog } from '@/src/stores/accounts/detection'
import {
  loginDialogOpenAtom,
  onboardingDialogAtom,
  openOnboardingDialog,
} from '@/src/stores/onboarding'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
  selectWalletsDialogChainTypeAtom,
  selectWalletsDialogOpenAtom,
} from '@/src/stores/select-wallets'
import { useSwapFormSubmit } from '@/src/stores/swap/actions'
import {
  destinationAccountStateAtom,
  fromTokenAcrossChainsAtom,
  openDestinationAccount,
  routeErrorsAtom,
  swapRequirementsAtom,
  swapRouteAtom,
  swapRouteQueryEnabledAtom,
  toTokenAcrossChainsAtom,
} from '@/src/stores/swap/atoms'
import { userAtom } from '@/src/stores/user/atoms'
import { ChangeAccountDialog } from '../accounts/change-account'
import { Button } from '../ui/button'
import { PriceImpactConfirmationAlert } from './price-impact-confirmation-alert'

export const SubmitButton = ({
  onBeforeSubmit,
}: {
  onBeforeSubmit?: () => void
}) => {
  const { trading, newLoginFlow, privacy, privacyUi } =
    useAtomValue(featureFlagsAtom)
  const user = useAtomValue(userAtom)
  const shouldDisplayOnboarding = user.id === 'guest' && newLoginFlow
  const setOnboardingDialog = useSetAtom(onboardingDialogAtom)

  const fromToken = useAtomValue(fromTokenAcrossChainsAtom)
  const toToken = useAtomValue(toTokenAcrossChainsAtom)
  const {
    fromBalanceAccount,
    isFromWalletNotConnected,
    isToWalletNotConnected,
    isToAccountNotSelected,
    userNeedsToGenerateHotkey,
    unsupportedChainByWallet,
    enabled,
    requiredUnconnectedWalletChainName,
    requiredBalanceAccountToSwitch,
    finalKeyringPendingAccount,
    isPrivacyVaultSwap,
    isToPrivacyVaultSwap,
  } = useAtomValue(swapRequirementsAtom)
  const setAddAccountDialog = usePendingAccountDialog()
  const { isLoading: isLoadingRoute } = useAtomValue(swapRouteAtom)
  const isQueryEnabled = useAtomValue(swapRouteQueryEnabledAtom)
  const { ampli } = useAmplitudeAnalytics()
  const { isLoading: isLoadingSubmit, onSubmit } = useSwapFormSubmit()
  const setChainType = useSetAtom(selectWalletsDialogChainTypeAtom)

  // Check for pending trades from history (same as activity tab)
  const { hasPendingTrade } = usePendingTrades()

  const shouldShowRouteLoading = isLoadingRoute && isQueryEnabled
  const routeErrors = useAtomValue(routeErrorsAtom)
  const [isPriceImpactDialogOpen, setIsPriceImpactDialogOpen] = useState(false)
  const [isChangeAccountDialogOpen, setIsChangeAccountDialogOpen] =
    useState(false)
  const { mutate, isPending } = useActivateAccount()
  const isWalletConnectDialogOpen = useAtomValue(selectWalletsDialogOpenAtom)
  const setDialogState = useSetAtom(selectWalletsDialogAtom)
  const setLoginDialogOpen = useSetAtom(loginDialogOpenAtom)
  const setDestinationAccountState = useSetAtom(destinationAccountStateAtom)

  const generateSwapHotkey = useCallback(() => {
    if (fromToken.data) {
      mutate({
        chainType: fromToken.data.requested_asset.chain_type,
      })
    }
  }, [fromToken.data, mutate])

  const tradeButtonOnClick = useCallback(() => {
    onBeforeSubmit?.()
    if (routeErrors?.priceImpactHigh) {
      setIsPriceImpactDialogOpen(true)
    } else {
      onSubmit()
    }
  }, [routeErrors?.priceImpactHigh, onSubmit, onBeforeSubmit])

  const onConnectWallet = useCallback(() => {
    ampli?.walletConnectWalletClicked({
      position: 'widget',
    })

    const chainType = isFromWalletNotConnected
      ? fromToken?.data?.requested_asset.chain_type
      : toToken?.data?.requested_asset.chain_type

    if (shouldDisplayOnboarding) {
      setOnboardingDialog(openOnboardingDialog('wallets'))
      setChainType(chainType)
      return
    }

    setDialogState(openSelectWalletsDialog(chainType))
  }, [
    ampli,
    isFromWalletNotConnected,
    fromToken?.data?.requested_asset.chain_type,
    toToken?.data?.requested_asset.chain_type,
    shouldDisplayOnboarding,
    setDialogState,
    setOnboardingDialog,
    setChainType,
  ])

  // Currently, we may not have access to the required to wallet keyring
  // to generate quotes, so we block the quote until the user switches.
  if (requiredBalanceAccountToSwitch && trading) {
    return (
      <>
        <Button
          size='lg'
          variant='primary'
          className='w-full'
          type='button'
          onClick={() => {
            if (requiredBalanceAccountToSwitch.toWallet) {
              // If we can just connect to the to wallet, do it
              requiredBalanceAccountToSwitch.toWallet.connect()
            } else {
              // Wallet already connected, has to account, prompt user to switch
              setIsChangeAccountDialogOpen(true)
            }
          }}
        >
          Continue
        </Button>
        <ChangeAccountDialog
          isOpen={isChangeAccountDialogOpen && !isWalletConnectDialogOpen}
          onOpenChange={setIsChangeAccountDialogOpen}
          leftWalletIcon={requiredBalanceAccountToSwitch.from?.logoUrl}
          leftWalletName={requiredBalanceAccountToSwitch.from?.displayName}
          leftAccountName={
            requiredBalanceAccountToSwitch.fromAccount?.name ??
            requiredBalanceAccountToSwitch.from?.accountName ??
            requiredBalanceAccountToSwitch.from?.displayName
          }
          rightWalletIcon={requiredBalanceAccountToSwitch.to.provider_info.img}
          rightWalletName={requiredBalanceAccountToSwitch.to.provider_info.name}
          rightAccountName={requiredBalanceAccountToSwitch.to.name}
          description={`Switch to the correct wallet account in your ${requiredBalanceAccountToSwitch.to.provider_info.name} browser extension to reconnect ${requiredBalanceAccountToSwitch.to.name}.`}
        />
      </>
    )
  }

  // Currently, we may not have access to the required to wallet keyring
  // to generate quotes, so we block the quote until the user switches.
  if (finalKeyringPendingAccount && trading) {
    return (
      <Button
        size='lg'
        variant='primary'
        className='w-full'
        type='button'
        onClick={() => {
          setAddAccountDialog(finalKeyringPendingAccount.grouping_key)
        }}
      >
        Continue
      </Button>
    )
  }

  if (fromBalanceAccount?.type === 'WatchAddress') {
    return (
      <Button
        size='lg'
        variant='primary'
        className='w-full'
        type='button'
        onClick={() => {
          setDialogState(
            openSelectWalletsDialog(
              fromToken?.data?.requested_asset.chain_type,
              fromBalanceAccount.address,
            ),
          )
        }}
      >
        Continue
      </Button>
    )
  }

  if ((isFromWalletNotConnected || isToWalletNotConnected) && trading) {
    return (
      <Button
        size='lg'
        variant='secondary'
        className='w-full'
        type='button'
        onClick={onConnectWallet}
      >
        <ClientOnly>
          Connect {requiredUnconnectedWalletChainName} wallet
        </ClientOnly>
      </Button>
    )
  }

  if (isToAccountNotSelected && toToken?.data && trading) {
    return (
      <Button
        size='lg'
        variant='secondary'
        className='w-full'
        type='button'
        onClick={() => {
          setDestinationAccountState(
            openDestinationAccount(toToken.data.requested_asset.chain_type),
          )
        }}
      >
        <ClientOnly>Select destination</ClientOnly>
      </Button>
    )
  }

  if (unsupportedChainByWallet && trading) {
    return (
      <Button
        size='lg'
        variant='secondary'
        className='w-full'
        type='button'
        onClick={onConnectWallet}
      >
        Change wallet
      </Button>
    )
  }

  if (userNeedsToGenerateHotkey && trading) {
    return (
      <ClientOnly>
        <Button
          type='button'
          size='lg'
          variant='primary'
          data-testid='hotkey'
          className='w-full'
          disabled={isPending}
          onClick={generateSwapHotkey}
        >
          Activate account
        </Button>
      </ClientOnly>
    )
  }

  // Privacy vault swap for guest users - show login/create account when wallet IS connected
  // This check comes AFTER the wallet connection checks above
  if (
    user.id === 'guest' &&
    (isPrivacyVaultSwap || isToPrivacyVaultSwap) &&
    trading &&
    privacy &&
    !isFromWalletNotConnected
  ) {
    // New privacyUi flow uses "Create account" text
    const buttonText = privacyUi ? 'Create account' : 'Login'

    if (!newLoginFlow) {
      return (
        <Button
          type='button'
          size='lg'
          variant='primary'
          data-testid='swap-login'
          className='w-full'
          asChild
        >
          <Link href='/login'>{buttonText}</Link>
        </Button>
      )
    }

    return (
      <ClientOnly>
        <Button
          type='button'
          size='lg'
          variant='primary'
          data-testid='swap-login'
          className='w-full'
          onClick={() => setLoginDialogOpen(true)}
        >
          {buttonText}
        </Button>
      </ClientOnly>
    )
  }

  return (
    <ClientOnly>
      <Button
        type='button'
        size='lg'
        variant='primary'
        data-testid='review-trade'
        className='w-full'
        disabled={
          !enabled ||
          isLoadingSubmit ||
          shouldShowRouteLoading ||
          hasPendingTrade
        }
        onClick={tradeButtonOnClick}
        tabIndex={0}
      >
        Trade
      </Button>

      <PriceImpactConfirmationAlert
        isOpen={isPriceImpactDialogOpen}
        setIsOpen={setIsPriceImpactDialogOpen}
      />
    </ClientOnly>
  )
}
